import { useRef, useState } from "react";
import formStyle from "../form.module.css";
import { X } from "lucide-react";
import SegmentedControl from "../SegmentedControl/SegmentedControl.tsx";
import { CategoryType, Pentest, TypeType } from "../../../../sdkStructs.ts";
import Tooltip from "../../../Common/Tooltips/InformationHover/InformationHover";


function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDateToday(): string {
  return formatDate(new Date());
}

function getDateNextWeek(): string {
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  return formatDate(nextWeek);
}

const emptyPentest: Pentest = {
  ID: 0,
  Title: "New Pentest",
  StartDate: getDateToday(),
  EndDate: getDateNextWeek(),
  Category: "blackbox",
  Type: "external",
  Client: "Acme Corporation",
  Scope: "",
};

interface Props {
  submit: (data: Pentest) => void;
  close?: () => void;

  formDefault?: Pentest;
}

function NewPentestForm({ formDefault, submit, close }: Readonly<Props>) {
  const [formState, setFormState] = useState<Pentest>(formDefault ?? emptyPentest);

  function preventedSubmit(event: React.FormEvent, data: Pentest) {
    event.preventDefault();
    submit(data);
  }

  return (
    <form onSubmit={(event: React.FormEvent) => preventedSubmit(event, formState)} className={formStyle.formWrapper}>
      <div className={formStyle.fieldsWrapper}>
        <h5 className={formStyle.firstLabel + " " + formStyle.requiredField}>{"Title"}</h5>
        <input
          className={formStyle.inputField}
          type={"text"}
          required
          value={formState.Title}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            setFormState({ ...formState, Title: e.currentTarget.value });
          }}
        />

        <h5 className={formStyle.label + " " + formStyle.requiredField}>{"Period"}</h5>
        <div className={formStyle.dateInterval}>
          <input
            className={formStyle.inputField + " " + formStyle.leftLink}
            type={"date"}
            required
            value={formState.StartDate}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setFormState({ ...formState, StartDate: e.currentTarget.value });
            }}
          />
          <input
            className={formStyle.inputField + " " + formStyle.rightLink}
            type={"date"}
            required
            value={formState.EndDate}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              setFormState({ ...formState, EndDate: e.currentTarget.value });
            }}
          />
        </div>

        <h5 className={formStyle.label}>{"Category"}</h5>
        <SegmentedControl
          defaultIndex={formState.Category === "blackbox" ? 0 : formState.Category === "whitebox" ? 2 : 1}
          name="category"
          callback={(val: string) =>
            setFormState({ ...formState, Category: val as CategoryType })
          }
          controlRef={useRef<HTMLDivElement>(null)}
          segments={[
            {
              label: "Blackbox",
              value: "blackbox",
              ref: useRef<HTMLDivElement>(null),
            },
            {
              label: "Graybox",
              value: "graybox",
              ref: useRef<HTMLDivElement>(null),
            },
            {
              label: "Whitebox",
              value: "whitebox",
              ref: useRef<HTMLDivElement>(null),
            },
          ]}
        />

        <h5 className={formStyle.label}>{"Type"}</h5>
        <SegmentedControl
          name="category"
          defaultIndex={formState.Type === "internal" ? 1 : 0}
          callback={(val: string) =>
            setFormState({ ...formState, Type: val as TypeType })
          }
          controlRef={useRef<HTMLDivElement>(null)}
          segments={[
            {
              label: "External",
              value: "external",
              ref: useRef<HTMLDivElement>(null),
            },
            {
              label: "Internal",
              value: "internal",
              ref: useRef<HTMLDivElement>(null),
            },
          ]}
        />

        <h5 className={formStyle.label + " " + formStyle.requiredField}>{"Client Company"}</h5>
        <input
          className={formStyle.inputField}
          type={"text"}
          required
          value={formState.Client}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            setFormState({ ...formState, Client: e.currentTarget.value });
          }}
        />
        <div className={formStyle.scopeWrapper}>
          <h5 className={formStyle.label}>{"Scope"}</h5>
          <Tooltip description="Example: Web Application and Network Infrastructure" />
        </div>
        <textarea
          className={formStyle.inputField}
          value={formState.Scope}
          onChange={(e: React.FormEvent<HTMLTextAreaElement>) => {
            setFormState({ ...formState, Scope: e.currentTarget.value });
          }}
        />
      </div>
      <button type="submit" className={formStyle.submitButton}>
        Submit
      </button>
      {close && (
        <button className={formStyle.closeButton} onClick={close}>
          <X stroke="var(--onedark-text)" strokeWidth="1.5" />
        </button>
      )}
    </form>
  );
}

export default NewPentestForm;
